import GoogleMapReact from "google-map-react";
import { useRouter } from "next/router";
import { useEffect, useLayoutEffect, useState } from "react";
import { useDetectCurrentLocation } from "../../../services/hooks/useDetectCurrentLocation";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { setLayoutProps } from "../../../services/redux/slices/LayoutSlice";
import { setSelectedFranchise } from "../../../services/redux/slices/configSlice";
import {
  setCurrentDeliveryConfig,
  setSelectedAddress,
  setTempLocation,
} from "../../../services/redux/slices/deliverySlice";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  increasePendingOperations,
  pushToLoadingStack,
  setBottomSheetState,
  setLoading,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { setQueryParam } from "../../../services/redux/slices/urlParamsSlice";
import { OrderTypesEnum, PagesEnum } from "../../../utils/enums";
import envConfig from "../../../utils/env.config";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import { RestaurantTypeEnum } from "../../../utils/types";
import { selectCurrentDeliveryConfig } from "../../../services/redux/selectors/deliverySelectors";

export const useMapScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslationI18();
  const router = useRouter();
  const { out_of_zone, next_page, franchise, order_type } = router.query;

  const delivery = useAppSelector((state) => state.delivery);
  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const mainLayoutData = useAppSelector((state) => state.layout.layoutProps);
  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  const isEcommerce: boolean = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const backPage = isEcommerce ? PagesEnum.Home : delivery.backPage;
  const restaurantConfig = restaurantInfo?.config;
  const enableInternalMaps = restaurantConfig?.enableInternalMaps ?? false;
  const enableNewDeliveryExperience =
    restaurantConfig?.enableNewDeliveryExperience ?? false;

  const initialZoom = enableNewDeliveryExperience ? 8 : 15;

  const [detailsSection, setDetailsSection] = useState(false);
  const [error, setError] = useState<{
    text: string;
    success?: boolean;
  } | null>(null);
  const [googleApiLoaded, setGoogleApiLoaded] = useState(false);
  const [mapLibreLoaded, setMapLibreLoaded] = useState(false);
  const [center, setCenter] = useState<{ lat: any; lng: any }>({
    lat: +delivery.tempLocation?.lat || delivery.selectedAddress?.lat || null,
    lng: +delivery.tempLocation?.lng || delivery.selectedAddress?.lng || null,
  });
  const [zoom, setZoom] = useState(initialZoom);

  const isMapLoaded =
    (!enableInternalMaps && googleApiLoaded) ||
    (enableInternalMaps && mapLibreLoaded);

  const egyptDefaultProps = {
    center: { lat: 30.0529366, lng: 31.3383273 },
    zoom: initialZoom,
  };

  const createMapOptions = {
    fullscreenControl: false,
    zoomControl: false,
    mapId: envConfig.googleMapsId,
    gestureHandling: "greedy",
    disableDefaultUI: true,
  };

  const outsideDeliveryTranslation = t("delivery:outside-delivery");
  const headerBackTranslation = t("common:header-back");
  const newAddressTranslation = t("delivery:new-address");
  const addLocationTranslation = t("delivery:add-location");
  const skip = t("delivery:skip");
  const skipTranslation = skip.charAt(0).toUpperCase() + skip.slice(1);

  const { getCurrentPosition } = useDetectCurrentLocation({
    onCenterChange: (newCenter) => {
      setCenter(newCenter);
      setZoom(17);
    },
    onErrorStateChange: setError,
    functionName: "mapScreen",
  });

  // so bad please enhance #1805
  useEffect(() => {
    if (isEcommerce && next_page) {
      if (delivery.selectedAddress?.area) {
        setDetailsSection(true);
      }
    }
    if (out_of_zone) {
      setError({ text: outsideDeliveryTranslation });
    }
  }, [isEcommerce]);

  useLayoutEffect(() => {
    dispatch(
      setLayoutProps({
        ...mainLayoutData,
        headerProps: {
          ...mainLayoutData.headerProps,
          display: false,
        },
        footerProps: {
          ...mainLayoutData.footerProps,
          display: false,
        },
      })
    );
  }, [mainLayoutData.headerProps.display]);

  useEffect(() => {
    if (currentDeliveryConfig) {
      setCenter(currentDeliveryConfig.location);
    }
  }, []);

  useEffect(() => {
    if (restaurantConfig?.loadingStateSolution) {
      const isLoaded = enableInternalMaps ? mapLibreLoaded : googleApiLoaded;
      dispatch(
        pushToLoadingStack({
          action: !isLoaded
            ? LoadingActionEnum.INCREASE
            : LoadingActionEnum.DECREASE,
          functionName: "MapScreen mapLoaded",
          date: new Date(),
        })
      );
      !isLoaded
        ? dispatch(increasePendingOperations())
        : dispatch(decreasePendingOperations());
    } else {
      const isLoaded = enableInternalMaps ? mapLibreLoaded : googleApiLoaded;
      dispatch(setLoading(!isLoaded));
    }
  }, [googleApiLoaded, mapLibreLoaded, enableInternalMaps]);

  useEffect(() => {
    if (
      franchise &&
      franchise !== "undefined" &&
      order_type &&
      order_type !== "undefined"
    ) {
      const franchiseDetails = restaurantInfo?.franchises.find(
        (f) => f.slug === franchise
      );
      dispatch(setSelectedFranchise(franchiseDetails));
      dispatch(setQueryParam(order_type as OrderTypesEnum));
    }
  }, []);

  const onChange = (value: GoogleMapReact.ChangeEventValue) => {
    if (
      delivery.tempLocation.lat &&
      delivery.tempLocation.lng &&
      !(
        `${value.center.lat}`.slice(0, 7) ===
          delivery.tempLocation?.lat?.toString().slice(0, 7) &&
        `${value.center.lng}`.slice(0, 7) ===
          delivery.tempLocation?.lng?.toString().slice(0, 7)
      )
    ) {
      dispatch(setSelectedAddress(null));
    }
    setZoom(value.zoom);
    setCenter(value.center);
    dispatch(
      setTempLocation({
        lat: value.center.lat,
        lng: value.center.lng,
      })
    );
    setError(null);
  };

  const onMapLibreChange = ({
    center: newCenter,
    zoom: newZoom,
  }: {
    center: { lat: number; lng: number };
    zoom: number;
  }) => {
    if (
      delivery.tempLocation.lat &&
      delivery.tempLocation.lng &&
      !(
        `${newCenter.lat}`.slice(0, 7) ===
          delivery.tempLocation?.lat?.toString().slice(0, 7) &&
        `${newCenter.lng}`.slice(0, 7) ===
          delivery.tempLocation?.lng?.toString().slice(0, 7)
      )
    ) {
      dispatch(setSelectedAddress(null));
    }
    setZoom(newZoom);
    setCenter(newCenter);
    dispatch(
      setTempLocation({
        lat: newCenter.lat,
        lng: newCenter.lng,
      })
    );
    setError(null);
  };

  const handleBack = () => {
    if (detailsSection) {
      setDetailsSection(false);
    } else {
      if (!order_type) {
        dispatch(setBottomSheetState(true));
        dispatch(setCurrentDeliveryConfig(null));
        router.push(
          getPageLink(router, backPage, { resetQueryParams: true }).href,
          getPageLink(router, backPage, { resetQueryParams: true }).as
        );
      } else {
        router.push(`${getPageLink(router, backPage).as}`);
      }
    }
  };

  const handleSkip = () => {
    dispatch(setSelectedAddress(null));
    if (detailsSection) {
      setDetailsSection(false);
    } else {
      router.push(`${getPageLink(router, PagesEnum.Home).as}`);
    }
  };

  return {
    // state
    delivery,
    currentDeliveryConfig,
    detailsSection,
    setDetailsSection,
    error,
    setError,
    googleApiLoaded,
    setGoogleApiLoaded,
    mapLibreLoaded,
    setMapLibreLoaded,
    center,
    setCenter,
    zoom,
    setZoom,
    // computed
    isMapLoaded,
    egyptDefaultProps,
    createMapOptions,
    enableInternalMaps,
    enableNewDeliveryExperience,
    franchiseConfig,
    restaurantInfo,
    // handlers
    onChange,
    onMapLibreChange,
    handleBack,
    handleSkip,
    getCurrentPosition,
    // translations
    headerBackTranslation,
    newAddressTranslation,
    addLocationTranslation,
    skipTranslation,
    t,
  };
};
